import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { addPadAttachment, createPadEntry, removePadAttachment, updatePadEntry, uploadPadAttachmentFile } from "./api";
import PadAttachments from "./PadAttachments";
import {
  PAD_CATEGORIES,
  ALTERNATE_METHODS,
  STOP_REASONS,
  categoryMeta,
  totalRentFromItem,
  rentBreakdownFromItem,
  moveOutDateFromItem,
  leaseStartDateFromItem,
  money,
  monthOf,
} from "./padInfoTypes";

// Visual tokens, shared with the addendum modal so the two sections read as one product.
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};
const T = { title: 16, body: 13, label: 12, hint: 11 };

// The confirmation card shows user-typed values (names, reasons, notes) inside Swal HTML.
const escapeText = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// The delay charge as typed → dollars with cents, or null when it is not a number yet.
// Mirrors normalizeEntryPayload on the server; both round to cents.
const parseCharge = (v) => {
  const raw = String(v ?? "").replace(/[^0-9.]/g, "");
  if (!raw) return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

const styles = {
  body: { padding: "16px 18px 18px", maxHeight: "72vh", overflowY: "auto", background: "#fff" },
  label: { fontSize: T.label, fontWeight: 600, color: C.text, marginBottom: 5, display: "block", lineHeight: 1.3 },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 8, height: 38,
    border: `1px solid ${C.inputBorder}`, borderRadius: 9, padding: "0 10px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: T.body, color: C.text, height: "100%", fontFamily: "inherit", minWidth: 0,
  },
  hint: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.4 },
};

// A label over a control. No helper sentences: the label says what the field is, the
// validation message in the footer says what is missing. `labelExtra` sits in small
// just right of the label text (the N/A toggle lives there) — as a SIBLING of the
// label, never inside it: a label wrapping a button becomes that button's name and
// forwards every click on the label text to it.
const Field = ({ label, labelExtra, children, style }) => (
  <div style={style}>
    {labelExtra ? (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
        <label style={{ ...styles.label, marginBottom: 0 }}>{label}</label>
        {labelExtra}
      </div>
    ) : (
      <label style={styles.label}>{label}</label>
    )}
    {children}
  </div>
);

// The small N/A pill beside a resume-date label: an explicit "no date on purpose",
// as opposed to a date nobody has picked yet.
const NaToggle = ({ on, onClick, disabled }) => (
  <button type="button" className={`pim-na${on ? " on" : ""}`} aria-pressed={on} onClick={onClick} disabled={disabled}>
    N/A
  </button>
);

const Box = ({ children, className = "", style, title }) => (
  <div className={`pim-focusable ${className}`} style={{ ...styles.fieldBox, ...style }} title={title || undefined}>
    {children}
  </div>
);

/**
 * The category picker: every option stays visible, with its one-line description, so the
 * first (and most important) decision in the form is made by reading rather than by opening
 * a menu. It is a radio group, not a dropdown: nothing floats, so nothing can be clipped by
 * the scrolling body.
 */
const CategoryPicker = ({ value, onPick, disabled, labelledBy }) => (
  <div className="pim-cats" role="radiogroup" aria-labelledby={labelledBy}>
    {PAD_CATEGORIES.map((c) => {
      const on = c.value === value;
      return (
        <button
          key={c.value}
          type="button"
          role="radio"
          aria-checked={on}
          className={`pim-cat${on ? " on" : ""}`}
          disabled={disabled}
          onClick={() => onPick(c.value)}
        >
          <span className="ic" style={{ background: c.bg, color: c.color }}>
            <FeatherIcon icon={c.icon} size={15} />
          </span>
          <span className="tx">
            <span className="t">{c.label}</span>
            <span className="b">{c.blurb}</span>
          </span>
          <span className="ck" aria-hidden="true">
            {on ? <FeatherIcon icon="check" size={11} strokeWidth={3} /> : null}
          </span>
        </button>
      );
    })}
  </div>
);

// `na` is form state only, never sent: it marks "no resume date on purpose" (the pull
// stays delayed / stopped until further notice) and is stored as an empty resume_date.
const emptyForm = (tenantNames = "") => ({
  tenant_names: tenantNames,
  category: "",
  effective_date: "",
  new_pad: { lease_start_date: "", reason: "" },
  transfer: { reason: "" },
  delay: { month: "", resume_date: "", charge: "", na: false },
  temp_stop: { alternate_method: "", resume_date: "", na: false },
  stop: { reason: "", move_out_date: "" },
  notes: "",
});

const fromExisting = (e) => ({
  tenant_names: e.tenant_names || "",
  category: e.category || "",
  effective_date: e.effective_date || "",
  new_pad: { lease_start_date: "", reason: "", ...(e.new_pad || {}) },
  transfer: { reason: "", ...(e.transfer || {}) },
  // A saved entry with no resume date was recorded as open-ended — that IS the N/A state.
  delay: { month: "", resume_date: "", ...(e.delay || {}), charge: e.delay?.charge === 0 || e.delay?.charge ? String(e.delay.charge) : "", na: e.category === "DELAY" && !(e.delay && e.delay.resume_date) },
  temp_stop: { alternate_method: "", resume_date: "", ...(e.temp_stop || {}), na: e.category === "TEMP_STOP" && !(e.temp_stop && e.temp_stop.resume_date) },
  stop: { reason: "", move_out_date: "", ...(e.stop || {}) },
  notes: e.notes || "",
});

/**
 * Create / edit one PAD Info entry. One surface, sized for small laptop screens: the category
 * list with the attachments panel beside it, the category's fields below, then notes. No money
 * and no banking details are captured here: the rent is read back from the unit's mastersheet
 * row, and account information never enters this system.
 *
 * The resident is NOT a field. It already sits in the header, and it is never editable. The
 * names still travel in the payload (a New PAD takes the incoming lease's tenants).
 */
const PadInfoModal = ({ isOpen, toggle, itemId, item, unitNo, buildingName, propertyName, tenantNames, incoming, existing, rentChange, onSaved }) => {
  const [form, setForm] = useState(() => (existing ? fromExisting(existing) : emptyForm(tenantNames)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Attachments are staged, not uploaded on pick: a brand-new entry has nothing to hang a file
  // on yet, and Cancel must not leave an orphaned object in the bucket. Everything is applied
  // in one go by save().
  const [stagedFiles, setStagedFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [savingStep, setSavingStep] = useState("");
  // If the entry saves but an upload then fails, the entry still exists. Remembering its id
  // means pressing Save again edits that entry rather than creating a second one.
  const [createdId, setCreatedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(existing ? fromExisting(existing) : emptyForm(tenantNames));
      setError("");
      setStagedFiles([]);
      setRemovedAttachmentIds([]);
      setSavingStep("");
      setCreatedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?._id]);

  const meta = form.category ? categoryMeta(form.category) : null;
  const fields = meta ? meta.fields : [];
  // Rent is never entered or stored here: it is always the mastersheet's number.
  const totalRent = useMemo(() => totalRentFromItem(item), [item]);
  const rentBreakdown = useMemo(() => rentBreakdownFromItem(item), [item]);
  // A PAD Stop pre-fills its move-out date from the lease end column, but stays editable.
  const systemMoveOut = useMemo(() => moveOutDateFromItem(item), [item]);
  // A New PAD's first pull is the lease start: the incoming signed lease's date when there is
  // one, else the mastersheet row's lease start column. Moving the pull off it needs a reason.
  const systemLeaseStart = useMemo(
    () => (incoming && incoming.start_date) || leaseStartDateFromItem(item) || "",
    [incoming, item]
  );
  const firstPullEdited =
    form.category === "NEW" && !!systemLeaseStart && !!form.effective_date && form.effective_date !== systemLeaseStart;
  // A New Rent Amount is about the unit's last addendum: that is where its amount and its date
  // come from. `rentChange` is the server's reading of that addendum (null when there is none).
  const fromAddendum = form.category === "AMOUNT_CHANGE" && rentChange && rentChange.new_rent != null ? rentChange : null;
  // The delay surcharge as currently typed — folded into the shown total so what the form
  // displays is what the delayed months are actually charged.
  const delayCharge = form.category === "DELAY" ? parseCharge(form.delay.charge) || 0 : 0;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSub = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const pickCategory = (value) => {
    // Switching category rebuilds the date state from scratch: nothing prefilled for one
    // category may survive into another (a New PAD's lease start used to stick to whatever
    // was picked next). Only two categories prefill at all: a New PAD starts at the lease
    // start, a PAD Stop at the lease end. Every other date is typed by hand — including a
    // New Rent Amount's, whose addendum still supplies the amount but no longer the date.
    const blank = emptyForm(form.tenant_names);
    const patch = {
      category: value,
      effective_date: "",
      notes: "",
      new_pad: blank.new_pad,
      transfer: blank.transfer,
      delay: blank.delay,
      temp_stop: blank.temp_stop,
      stop: blank.stop,
    };
    // A New PAD is usually for the incoming resident; the row may still show the departing one.
    if (value === "NEW" && !existing && incoming?.tenants && (!form.tenant_names || form.tenant_names === tenantNames)) patch.tenant_names = incoming.tenants;
    if (value !== "NEW" && !existing && form.tenant_names === incoming?.tenants) patch.tenant_names = tenantNames || "";
    if (value === "NEW" && systemLeaseStart) patch.effective_date = systemLeaseStart;
    if (value === "STOP" && systemMoveOut) {
      patch.effective_date = systemMoveOut;
      patch.stop = { ...blank.stop, move_out_date: systemMoveOut };
    }
    // Only a New PAD and a PAD Change carry paperwork; anything staged for one of those
    // must not ride silently into a category that will never show the panel again.
    if (value !== "NEW" && value !== "CHANGE") {
      setStagedFiles([]);
      setRemovedAttachmentIds([]);
    }
    set(patch);
    if (error) setError("");
  };

  // Only a New PAD and a PAD Change carry paperwork here. The other categories hide the
  // panel entirely — files already saved on an old entry stay on the record and keep
  // showing in the table and the report, just not in this modal.
  const wantsAttachments = form.category === "NEW" || form.category === "CHANGE";

  // Documents still on the entry after the pending removals apply.
  const keptSavedCount = (existing?.attachments || []).filter(
    (a) => a && !a.removed && !removedAttachmentIds.includes(a.attachment_id)
  ).length;

  const validate = () => {
    if (!form.category) return "Pick what kind of PAD update this is.";
    if (!form.effective_date) return "The effective date is required.";
    if (form.category === "NEW" && firstPullEdited && !form.new_pad.reason.trim()) {
      return `The first pull differs from the lease start (${systemLeaseStart}) — give the reason, e.g. incentive for first month.`;
    }
    if (form.category === "DELAY" && parseCharge(form.delay.charge) === null) return "Enter the delay charge in dollars — 0 if there is none.";
    if (form.category === "DELAY" && !form.delay.na && !form.delay.resume_date) return "When does the PAD continue regularly? Pick a date, or N/A if it stays on the delayed date.";
    if (form.category === "DELAY" && form.delay.resume_date && form.delay.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && !form.temp_stop.na && !form.temp_stop.resume_date) return "When does the PAD resume? Pick a date, or N/A if it stays stopped until the next notice.";
    if (form.category === "TEMP_STOP" && form.temp_stop.resume_date && form.temp_stop.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && !form.temp_stop.alternate_method) return "How is the resident paying meanwhile?";
    if (form.category === "STOP" && !form.stop.reason) return "Please pick a reason for stopping the PAD.";
    // A New PAD and a PAD Change need their paperwork: the email, notice or signed form
    // behind the change. The other categories carry no documents here at all.
    if (wantsAttachments && keptSavedCount + stagedFiles.length === 0) return "Attach at least one document: the paperwork behind this change.";
    return "";
  };

  const save = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    // One last look before it becomes part of the unit's PAD record: everything the entry
    // will say, in one card, so a wrong date or resident is caught here and not by accounting.
    const catMeta = categoryMeta(form.category);
    const summaryRows = [
      ["Unit", [unitNo ? `Unit ${unitNo}` : "", buildingName, propertyName].filter(Boolean).join(" · ")],
      ["Resident", form.tenant_names || tenantNames || "—"],
      [catMeta.dateLabel || "Effective", form.effective_date],
      ...(form.category === "NEW" && firstPullEdited ? [["Reason", form.new_pad.reason.trim()]] : []),
      ...(form.category === "TRANSFER" ? [["Reason", form.transfer.reason.trim() || "—"]] : []),
      ...(form.category === "DELAY" ? [
        ["Delay charge", money(parseCharge(form.delay.charge) || 0)],
        ...(totalRent !== null && parseCharge(form.delay.charge) > 0 ? [["Total to charge", `${money(totalRent + parseCharge(form.delay.charge))} (${money(totalRent)} rent + ${money(parseCharge(form.delay.charge))} delay charge)`]] : []),
        ["Resumes", form.delay.na ? "Same delayed date for the upcoming months" : form.delay.resume_date],
      ] : []),
      ...(form.category === "TEMP_STOP" ? [
        ["Resumes", form.temp_stop.na ? "Stopped until the next notice" : form.temp_stop.resume_date],
        ["Paying by", form.temp_stop.alternate_method],
      ] : []),
      ...(form.category === "STOP" ? [
        ["Reason", (STOP_REASONS.find((r) => r.value === form.stop.reason) || {}).label || form.stop.reason],
        ["Move-out", form.stop.move_out_date || "—"],
      ] : []),
      ...(wantsAttachments ? [["Attachments", String(keptSavedCount + stagedFiles.length)]] : []),
      ...(form.notes ? [["Notes", form.notes]] : []),
    ];
    const confirmed = await Swal.fire({
      title: "Save this PAD update?",
      html:
        `<div style="text-align:left;font-size:0.92rem;">` +
        `<p style="color:#64748B;margin:0 0 12px;">Please confirm everything below is correct — this becomes part of the unit's PAD record.</p>` +
        `<div style="display:inline-flex;align-items:center;gap:6px;background:${catMeta.bg};color:${catMeta.color};font-weight:700;border-radius:999px;padding:4px 12px;margin-bottom:10px;">${catMeta.label}</div>` +
        `<table style="width:100%;border-collapse:collapse;">` +
        summaryRows.map(([k, v]) =>
          `<tr><td style="color:#64748B;padding:3px 12px 3px 0;white-space:nowrap;vertical-align:top;">${k}</td><td style="font-weight:600;overflow-wrap:anywhere;">${escapeText(v)}</td></tr>`
        ).join("") +
        `</table></div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, save it",
      cancelButtonText: "Go back",
      confirmButtonColor: "#0C447C",
      reverseButtons: true,
    });
    if (!confirmed.isConfirmed) return;

    setSaving(true);
    setError("");
    const payload = {
      mastersheet_item_id: itemId,
      category: form.category,
      effective_date: form.effective_date,
      // The bank always pulls on the 1st; a Delay moves that month's pull to the chosen day.
      pull_day: form.category === "DELAY" ? Number(form.effective_date.slice(8, 10)) || 1 : 1,
      notes: form.notes,
      tenant_names: (form.tenant_names || tenantNames || "").trim(),
    };
    // The lease start it was pre-filled from rides along, so the record says what the first
    // pull was moved FROM; the reason only matters when it actually moved.
    if (form.category === "NEW") payload.new_pad = {
      lease_start_date: systemLeaseStart || "",
      reason: firstPullEdited ? form.new_pad.reason.trim() : "",
    };
    // A Transfer PAD is a New PAD without paperwork: no attachments, and the reason —
    // when one was given — travels with the entry and shows on the report.
    if (form.category === "TRANSFER") payload.transfer = { reason: form.transfer.reason.trim() };
    // N/A is stored as an empty resume date: the delayed date holds for the upcoming months.
    if (form.category === "DELAY") payload.delay = { month: monthOf(form.effective_date), resume_date: form.delay.na ? "" : form.delay.resume_date, charge: parseCharge(form.delay.charge) };
    if (form.category === "TEMP_STOP") payload.temp_stop = { alternate_method: form.temp_stop.alternate_method, resume_date: form.temp_stop.na ? "" : form.temp_stop.resume_date };
    if (form.category === "STOP") payload.stop = form.stop;
    // The link, never the amount: the server reads the number off the addendum every time.
    if (form.category === "AMOUNT_CHANGE") payload.lease_addendum_id = rentChange?.addendum_oid || null;
    try {
      // 1. The entry itself: a new one has to exist before anything can hang off it.
      setSavingStep(stagedFiles.length || removedAttachmentIds.length ? "Saving entry…" : "");
      let entryId = existing?._id || createdId;
      if (entryId) {
        await updatePadEntry(entryId, payload);
      } else {
        const res = await createPadEntry(payload);
        entryId = res?.data?.data?._id;
        if (!entryId) {
          if (stagedFiles.length) throw new Error("The entry saved but did not return an id, so the files were not attached.");
        } else {
          setCreatedId(entryId);
        }
      }

      // 2. Removals before uploads, so swapping a file for a better one cannot trip the
      //    ten-attachment ceiling.
      for (const attachmentId of [...removedAttachmentIds]) {
        setSavingStep("Removing attachments…");
        await removePadAttachment(entryId, attachmentId);
        // Drop it as it lands: a retry after a later failure must not repeat finished work.
        setRemovedAttachmentIds((prev) => prev.filter((x) => x !== attachmentId));
      }

      // 3. Upload each staged file, then record it against the entry.
      const toUpload = [...stagedFiles];
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        setSavingStep(`Uploading ${i + 1} of ${toUpload.length}…`);
        const uploaded = await uploadPadAttachmentFile(file);
        await addPadAttachment(entryId, {
          key: uploaded.key,
          filename: file.name,
          size: file.size,
          mime: file.type,
        });
        setStagedFiles((prev) => prev.filter((f) => f !== file));
      }

      const n = toUpload.length;
      toast.success(
        `${existing?._id ? "PAD info updated" : "PAD info recorded"}${n ? ` · ${n} file${n === 1 ? "" : "s"} attached` : ""}`
      );
      onSaved && onSaved();
      toggle();
    } catch (e) {
      const base = e?.response?.data?.message || e.message || "Failed to save PAD info";
      // Say plainly that the entry is already saved, or someone will re-type the whole thing.
      const msg = createdId || existing?._id ? `${base} The entry itself is saved. Press Save again to finish the attachments.` : base;
      setError(msg);
      toast.error(base);
    } finally {
      setSaving(false);
      setSavingStep("");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="pad-info-modal-dialog" contentClassName="pad-info-modal-content">
      <style>{`
        /* A bit wider than lg so the two-column top row breathes; still fits a 13" laptop. */
        .pad-info-modal-dialog { max-width: 860px; }
        .pad-info-modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .pad-info-modal-content input::placeholder, .pad-info-modal-content textarea::placeholder { color: #9AA7B8; }
        .pad-info-modal-content select:focus, .pad-info-modal-content input:focus, .pad-info-modal-content textarea:focus { outline: none; }
        .pim-focusable:focus-within { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.12); }

        /* Category list on the left, attachments on the right; the right panel stretches to
           the list's height so the two columns always share one bottom edge. */
        .pim-toprow { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr); gap: 16px; align-items: stretch; }
        .pim-col { display: flex; flex-direction: column; min-width: 0; }
        .pim-col > :last-child { flex: 1; min-height: 0; }
        @media (max-width: 640px) { .pim-toprow { grid-template-columns: 1fr; } }

        /* The category radio group. */
        .pim-cats { border: 1px solid ${C.inputBorder}; border-radius: 10px; background: #fff; overflow: hidden; }
        .pim-cat { display: flex; align-items: center; gap: 10px; width: 100%; padding: 7px 10px; border: none;
          border-top: 1px solid ${C.border}; background: none; cursor: pointer; font-family: inherit; text-align: left;
          transition: background .12s; }
        .pim-cat:first-child { border-top: none; }
        .pim-cat:hover:not(:disabled) { background: ${C.soft}; }
        .pim-cat.on, .pim-cat.on:hover { background: #EFF6FD; }
        .pim-cat:focus-visible { outline: none; box-shadow: inset 0 0 0 2px rgba(26,111,181,0.35); }
        .pim-cat:disabled { cursor: not-allowed; opacity: .6; }
        .pim-cat .ic { width: 28px; height: 28px; border-radius: 8px; flex: none; display: inline-flex;
          align-items: center; justify-content: center; }
        .pim-cat .tx { flex: 1; min-width: 0; }
        .pim-cat .t { display: block; font-size: 13px; font-weight: 700; color: ${C.text}; line-height: 1.2; }
        .pim-cat .b { display: block; font-size: 11.5px; color: ${C.muted}; line-height: 1.3; margin-top: 2px; }
        .pim-cat .ck { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid ${C.inputBorder}; flex: none;
          display: inline-flex; align-items: center; justify-content: center; color: #fff; background: #fff;
          transition: background .12s, border-color .12s; }
        .pim-cat.on .ck { background: ${C.primaryMd}; border-color: ${C.primaryMd}; }

        .pim-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; margin-top: 16px; }
        .pim-ref { flex: none; max-width: 45%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10.5px;
          color: ${C.muted}; font-family: SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace; letter-spacing: .02em; }
        .pim-btn { border: none; cursor: pointer; font-size: 13px; font-weight: 600; border-radius: 8px;
          padding: 9px 16px; display: inline-flex; align-items: center; gap: 7px; font-family: inherit; }
        .pim-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .pim-btn-primary:hover:not(:disabled) { background: #083460; }
        .pim-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .pim-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .pim-btn-ghost:hover { background: #F1F5FA; }
        .pim-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .pim-iconbtn:hover { color: ${C.text}; }
        .pim-select { border: none; outline: none; background: transparent; flex: 1; width: 100%; font-size: ${T.body}px; color: ${C.text}; height: 100%; font-family: inherit; }

        /* The small N/A pill beside a resume-date label. */
        .pim-na { border: 1px solid ${C.inputBorder}; background: #fff; color: ${C.muted}; font-size: 10px; font-weight: 700;
          letter-spacing: .05em; border-radius: 999px; padding: 1px 9px; line-height: 1.6; cursor: pointer;
          font-family: inherit; flex: none; transition: background .12s, color .12s, border-color .12s; }
        .pim-na:hover:not(:disabled) { border-color: ${C.primaryMd}; color: ${C.primaryMd}; }
        .pim-na.on { background: ${C.primaryMd}; border-color: ${C.primaryMd}; color: #fff; }
        .pim-na:disabled { cursor: not-allowed; opacity: .6; }
      `}</style>

      {/* ---- header: what this is, and who/where. The resident lives here, nowhere else ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 18px", borderBottom: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: "#EBF3FC", color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          <FeatherIcon icon="credit-card" size={17} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.title, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
            {existing?._id ? "Edit PAD info" : "Record PAD info"}
          </div>
          <div style={{ fontSize: T.hint, color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[unitNo ? `Unit ${unitNo}` : "", buildingName, propertyName, tenantNames].filter(Boolean).join(" · ")}
          </div>
        </div>
        <button type="button" className="pim-iconbtn" onClick={toggle} aria-label="Close">
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      <div style={styles.body}>
        {/* ---- category, with attachments beside it when this category carries paperwork ---- */}
        <div className={wantsAttachments ? "pim-toprow" : ""}>
          <div className="pim-col">
            <label id="pim-cat-label" style={styles.label}>What kind of PAD update is this?</label>
            <CategoryPicker value={form.category} onPick={pickCategory} disabled={saving} labelledBy="pim-cat-label" />
          </div>
          {wantsAttachments && (
            <div className="pim-col">
              <label style={styles.label}>Attachments</label>
              <PadAttachments
                fill
                entryId={existing?._id || createdId}
                saved={existing?.attachments || []}
                staged={stagedFiles}
                removedIds={removedAttachmentIds}
                onStage={(files) => {
                  setStagedFiles((prev) => [...prev, ...files]);
                  if (error) setError("");
                }}
                onUnstage={(i) => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                onToggleRemove={(id) =>
                  setRemovedAttachmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
                }
                unitNo={unitNo}
                propertyName={propertyName}
                entryCategoryLabel={meta?.label}
                entryDate={form.effective_date}
                canWrite
                busy={saving}
              />
            </div>
          )}
        </div>

        {/* ---- the fields this category needs ---- */}
        {meta && (
          <div className="pim-fields">
            <Field label={meta.dateLabel || "Effective from"}>
              <Box title={form.category === "NEW" && systemLeaseStart ? `Lease starts ${systemLeaseStart}` : undefined}>
                <FeatherIcon icon="calendar" size={14} color={C.muted} />
                <input type="date" style={styles.bareInput} value={form.effective_date} onChange={(e) => set({ effective_date: e.target.value })} />
              </Box>
            </Field>

            {/* The first pull was moved off the lease start — the reason is part of the record. */}
            {form.category === "NEW" && firstPullEdited && (
              <Field label={`Reason (lease starts ${systemLeaseStart})`}>
                <Box>
                  <FeatherIcon icon="edit-3" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. Incentive for first month"
                    maxLength={500}
                    value={form.new_pad.reason}
                    onChange={(e) => setSub("new_pad", { reason: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {/* A Transfer PAD's why — optional, but shown on the report wherever it was given. */}
            {fields.includes("transfer_reason") && (
              <Field label="Reason">
                <Box>
                  <FeatherIcon icon="edit-3" size={14} color={C.muted} />
                  <input
                    type="text"
                    style={styles.bareInput}
                    placeholder="e.g. Moved from another unit"
                    maxLength={500}
                    value={form.transfer.reason}
                    onChange={(e) => setSub("transfer", { reason: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {fields.includes("delay_resume") && (
              <Field
                label="PAD continues regularly on"
                labelExtra={
                  <NaToggle
                    on={form.delay.na}
                    disabled={saving}
                    onClick={() => setSub("delay", { na: !form.delay.na, resume_date: form.delay.na ? form.delay.resume_date : "" })}
                  />
                }
              >
                {form.delay.na ? (
                  <Box style={{ background: C.soft }} title="Select N/A when the same delayed date holds for all the upcoming months">
                    <FeatherIcon icon="repeat" size={14} color={C.muted} />
                    <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", color: C.muted }}>
                      Same delayed date for the upcoming months
                    </span>
                  </Box>
                ) : (
                  <Box>
                    <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                    <input type="date" style={styles.bareInput} value={form.delay.resume_date} onChange={(e) => setSub("delay", { resume_date: e.target.value })} />
                  </Box>
                )}
              </Field>
            )}

            {/* The delay surcharge: the one dollar figure an entry records, added to each
                delayed month's pull. Cents welcome, zero allowed — but it must be typed. */}
            {fields.includes("delay_resume") && (
              <Field label="Delay charge ($)">
                <Box>
                  <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    style={styles.bareInput}
                    value={form.delay.charge}
                    onChange={(e) => setSub("delay", { charge: e.target.value })}
                  />
                </Box>
              </Field>
            )}

            {fields.includes("temp_resume") && (
              <Field
                label="PAD resumes on"
                labelExtra={
                  <NaToggle
                    on={form.temp_stop.na}
                    disabled={saving}
                    onClick={() => setSub("temp_stop", { na: !form.temp_stop.na, resume_date: form.temp_stop.na ? form.temp_stop.resume_date : "" })}
                  />
                }
              >
                {form.temp_stop.na ? (
                  <Box style={{ background: C.soft }} title="Select N/A when the PAD stays stopped until the next notice">
                    <FeatherIcon icon="pause-circle" size={14} color={C.muted} />
                    <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", color: C.muted }}>
                      Stopped until the next notice
                    </span>
                  </Box>
                ) : (
                  <Box>
                    <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                    <input type="date" style={styles.bareInput} value={form.temp_stop.resume_date} onChange={(e) => setSub("temp_stop", { resume_date: e.target.value })} />
                  </Box>
                )}
              </Field>
            )}

            {fields.includes("temp_method") && (
              <Field label="Paying meanwhile by">
                <Box>
                  <select className="pim-select" value={form.temp_stop.alternate_method} onChange={(e) => setSub("temp_stop", { alternate_method: e.target.value })}>
                    <option value="">Select…</option>
                    {ALTERNATE_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </Box>
              </Field>
            )}

            {fields.includes("stop_reason") && (
              <Field label="Reason">
                <Box>
                  <select className="pim-select" value={form.stop.reason} onChange={(e) => setSub("stop", { reason: e.target.value })}>
                    <option value="">Select…</option>
                    {STOP_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Box>
              </Field>
            )}

            {fields.includes("stop_move_out") && (
              <Field label="Move-out date">
                <Box>
                  <FeatherIcon icon="log-out" size={14} color={C.muted} />
                  <input type="date" style={styles.bareInput} value={form.stop.move_out_date} onChange={(e) => setSub("stop", { move_out_date: e.target.value })} />
                </Box>
              </Field>
            )}

            {/* Total rent: read-only. A New Rent Amount shows what the last addendum sets; every
                other category shows the mastersheet's number. The breakdown or the addendum is
                the tooltip, not a sentence under the field. A PAD Stop shows no rent at all —
                the resident is moving out, so the number is noise. */}
            {form.category !== "STOP" && (
            <Field label={fromAddendum ? "New total rent" : delayCharge > 0 ? "Total rent (with delay charge)" : "Total rent"}>
              <Box
                style={{ background: C.soft }}
                title={
                  fromAddendum
                    ? `Per addendum ${fromAddendum.addendum_id || ""} effective ${fromAddendum.effective_date || "—"}${fromAddendum.previous_rent != null ? `; was ${money(fromAddendum.previous_rent)}` : ""}`
                    : delayCharge > 0 && totalRent !== null
                      ? `${money(totalRent)} rent + ${money(delayCharge)} delay charge`
                      : rentBreakdown.map((r) => `${r.label} ${money(r.amount)}`).join(" + ")
                }
              >
                <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", fontWeight: 700 }}>
                  {fromAddendum
                    ? money(fromAddendum.new_rent)
                    : totalRent === null
                      ? "—"
                      : money(totalRent + delayCharge)}
                </span>
                {fromAddendum?.addendum_id ? <span className="pim-ref">{fromAddendum.addendum_id}</span> : null}
                <FeatherIcon icon="lock" size={12} color={C.muted} />
              </Box>
            </Field>
            )}
          </div>
        )}

        {/* ---- notes ---- */}
        {meta && (
          <Field label="Notes for accounting" style={{ marginTop: 14 }}>
            <Box>
              <FeatherIcon icon="file-text" size={14} color={C.muted} />
              <input
                type="text"
                style={styles.bareInput}
                placeholder="e.g. Sending e-transfer for Jan"
                maxLength={4000}
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </Box>
          </Field>
        )}
      </div>

      {/* ---- footer ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderTop: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 7 }}>
          {error ? (
            <>
              <FeatherIcon icon="alert-circle" size={14} color="#A32D2D" style={{ flex: "none" }} />
              <span style={{ ...styles.hint, color: "#A32D2D", fontWeight: 600 }}>{error}</span>
            </>
          ) : null}
        </span>
        <div style={{ display: "flex", gap: 8, flex: "none" }}>
          <button
            type="button"
            className="pim-btn pim-btn-ghost"
            onClick={() => {
              // A failed save may have already created the entry; refresh so the list is honest.
              if (createdId) onSaved && onSaved();
              toggle();
            }}
            disabled={saving}
          >
            {createdId ? "Close" : "Cancel"}
          </button>
          <button type="button" className="pim-btn pim-btn-primary" onClick={save} disabled={saving || !meta}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={14} />}
            {saving && savingStep ? savingStep : existing?._id ? "Save changes" : "Record PAD info"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PadInfoModal;
